// The voice CALL SCREEN — the expanded posture of a voice session, behind the
// Expand button of the call bars (the in-composer VoiceStrip and the island).
// Mounted ONCE per app root next to ModalRoot (workspace/app.js always;
// canvas/app.js gated `if: !_inShell` so the shell's mount serves embedded
// canvas). Session mechanics (mic, the live voice agent of a talk call, the
// server voice engines — Whisper STT + 11labs TTS — and command routing) live
// in functions/voiceSession.js and in this component's `scope`; this
// component owns the screen:
//
//   • edge glow — the brand `themeDark` conic gradient, blurred and masked to
//     the viewport edges, hue-drifting and swelling with the live mic level
//   • the call (2026-10-22, the call redesign — the anatomy of the Fly call
//     Nika approved for shaker): who you are talking to and the clock across
//     the top with Minimize; the orb and the status word; the conversation as
//     captions; Stop speaking / Try again in a reserved slot; Type · End ·
//     Mute. All of it the SHARED call chrome (components/VoiceControls.js).
//
// ONE POSTURE since the island unification (T14, tickets/ai-fable.md): this
// overlay is the FULL immersive surface only. The island (SimoneReasoningPill)
// renders the minimized call. While minimized, THIS element stays mounted and
// keeps hosting the engine (scope handles, onStateUpdate watcher, Esc
// binding); only its visuals hide. The island reads the analyser straight off
// this element's scope via the same lookdown the engine uses.
//
// All 60 fps work happens in `onFrame` via `setNodeStyles` (never state), and
// show/hide is the ModalCard opacity pattern — `.isActive` bound to
// `s.root.voiceOpen` — so enter/exit transitions survive. The orb's ring
// carries NO transform transition on purpose: a running transition pins a
// property the frame loop rewrites constantly (see
// reference_css_transition_defeats_important).

// VoiceLangItem/VoiceLangMenu/VOICE_LANGS retired
// (VOICE-TALK-ONLY-REMOVE-DEAD-CONVAI-UI-1): voiceLang had no reader in the
// Simone loop — voiceTranscribe sends no language hint and
// voiceTts({ text }) sends none (Whisper auto-detects, per voiceSession.js's
// own header) — so the picker changed nothing a Talk user heard.

// Talk-mode turns are read straight off the live assistant thread, so the
// streamed reply APPENDS into a scrolling log instead of one line being
// replaced (2026-08-02 per Nika). `voiceChatKey` is the surface prefix the
// session captured at start ('' general | '_ax_' add-app | '_db_' dashboard) —
// the same namespacing sendPrompt uses.

// ——— voice ⟷ chat exclusivity (2026-08-06 per Nika "when voice is open, no
// point to have chat - they are morphing into each other") ———
//
// While a voice surface is in the FOREGROUND — the full overlay or a composer
// strip: `voiceOpen && (voiceHost || !voiceMinimized)` (the strip deliberately
// ignores the minimized axis, so `voiceHost` alone keeps it visible) — an open
// assistant drawer would stream the very same conversation beside it: command
// turns dispatch through sendPrompt into the exact thread the drawer renders,
// while the voice log mirrors them into `voiceTurns`. So on the foreground
// rising edge the drawer is docked to its EXISTING 'minimized' posture (the
// shipped MinimizeBtn path — pill-handle semantics, animated hide, grid column
// released) and restored when the session ENDS.
//
// Deliberately asymmetric: restore fires on the voiceOpen FALLING edge (session
// end), never on minimize — after _commitAssistant hands off to the island
// (`voiceMinimized: true`), the island IS the streaming surface, and
// re-expanding the drawer at that exact moment would put the same reply on
// screen twice again. The island's own close is the restore moment.
//
//   • plain root.update, never setAssistantDock — that helper persists the dock
//     to localStorage, and this is session choreography (the same distinction
//     _syncShellRoute's reveal-on-open documents in workspace/app.js).
//   • voiceHost === 'chat' is exempt: the strip renders INSIDE the drawer's own
//     composer there — the drawer IS the voice surface.
//   • edge-triggered via scope-held previous flags: a user who re-expands the
//     drawer mid-session wins — suppression asserts once at open, never again.
//   • memory lives on this element's scope (the session host every other voice
//     runtime handle lives on), NOT a new root key: it cannot outlive the
//     session, needs no state-registry pairing, and can never leak across
//     workspace switches.
//   • gated on root.assistantOpen being truthy — canvas/preview roots never
//     declare assistant keys (undeclared-root-key rule) so they never take a
//     write; the restore branch only runs if this helper minimized the drawer.

export const VoiceOverlay = {
  // Marks this element as the session host — voiceSession functions resolve it
  // via getRoot().lookdown('VoiceOverlay') and keep every runtime handle
  // (stream, analyser, recognition, utterance, timers) on this scope.
  //
  // FA204 / frank trap — `syncChatSuppression` used to be a bare module-scope
  // const read from inside `onStateUpdate` (a serialized factory). It read as
  // undefined once this component was frank.toJSON'd for platform delivery
  // (workspace d6ea1142, project_frank_drops_module_local_helpers). Moved
  // into `scope`, called via `el.scope.syncChatSuppression(el)`.
  scope: {
    __isVoiceOverlay: true,

    // Standard WAI-ARIA dialog-open focus contract for the FULL surface
    // (`role: 'dialog'`, `ariaModal: 'true'` below) — nothing moved focus
    // into or out of this overlay before (confirmed: zero `.focus()` calls
    // anywhere across the voice surface), so a keyboard/screen-reader user
    // pressing the mic got no confirmation the dialog had taken over, and
    // closing it dropped focus to nowhere. The RESTORE half lives in
    // voiceSession.js (`_restoreFocus`, called from every teardown path) —
    // this half only handles moving focus IN, since it is the one place
    // that actually knows when the isActive predicate flips true.
    //
    // Edge-triggered off the SAME boolean the CSS `.isActive` class uses
    // (never fires while already open, never fights the opacity/visibility
    // transition), exactly the `voicePrevForeground` pattern immediately
    // below — deliberately a SEPARATE prev-flag so the two edge detectors
    // can never interfere with each other.
    //
    // Targets the header's Minimize (CallMinimize, not the overlay root) — a
    // real, labelled, always-present next-action for a surface with no text
    // input of its own to focus, and the one an accidental Enter cannot
    // hang up on.
    //
    // WAITS FOR THE SURFACE TO BE VISIBLE, it does not assume one frame
    // (VOICE-OVERLAY-STATE-HOOK-DEAD-1 live measurement, 2026-08-22):
    // `focus()` on a descendant of a `visibility: hidden` element is a no-op,
    // and the `.isActive` class that lifts `visibility: hidden` lands SEVERAL
    // frames after the state edge — measured on the running shell, the node
    // still read `visibility: hidden` at rAF #1 (+152 ms) and rAF #2
    // (+541 ms) and only flipped at ~+940 ms. A single rAF therefore focused
    // nothing, which is exactly the residual the ticket reports
    // (`document.activeElement` stayed BODY). So: re-check on each frame,
    // for a bounded number of frames, and bail the moment the surface stops
    // being the full one or the user has already focused inside it.
    // `getComputedStyle` here is a style read of THIS element's OWN node (no
    // selector, no DOM walk) — the only honest way to know the transition
    // has actually made the dialog focusable.
    syncFocus(el) {
      const root = el.state && (el.state.root || el.state)
      if (!root) return
      const active = !!(root.voiceOpen && !root.voiceMinimized && !root.voiceHost)
      const sc = el.scope
      const wasActive = !!sc.voiceFocusPrevActive
      sc.voiceFocusPrevActive = active
      if (!active || wasActive) return
      // ~1 s of frames on a 60 fps box, and far longer on a loaded one —
      // every attempt re-reads the posture, so an overrun can never focus a
      // surface the user already dismissed.
      let framesLeft = 60
      const attempt = () => {
        if (!el.node || !el.node.isConnected) return
        const st = el.state && (el.state.root || el.state)
        if (!st || !st.voiceOpen || st.voiceMinimized || st.voiceHost) return
        const doc = el.node.ownerDocument
        const view = doc && doc.defaultView
        const hidden =
          view && typeof view.getComputedStyle === 'function'
            ? view.getComputedStyle(el.node).visibility === 'hidden'
            : false
        if (hidden) {
          if (framesLeft-- > 0) requestAnimationFrame(attempt)
          return
        }
        // A user who already reached inside the dialog keeps their focus.
        if (doc && doc.activeElement && el.node.contains(doc.activeElement)) return
        const safeBtn = el.lookdown && el.lookdown('CallMinimize')
        const target = (safeBtn && safeBtn.node) || el.node
        if (target && typeof target.focus === 'function') target.focus()
      }
      requestAnimationFrame(attempt)
    },

    syncChatSuppression(el) {
      const root = el.state && (el.state.root || el.state)
      if (!root || !root.update) return
      const sc = el.scope
      const foreground = !!(root.voiceOpen && (root.voiceHost || !root.voiceMinimized))
      const open = !!root.voiceOpen
      const wasForeground = !!sc.voicePrevForeground
      const wasOpen = !!sc.voicePrevOpen
      sc.voicePrevForeground = foreground
      sc.voicePrevOpen = open
      if (foreground && !wasForeground) {
        if (root.assistantOpen && root.assistantDock !== 'minimized' && root.voiceHost !== 'chat') {
          sc.voiceDockRestore = root.assistantDock || 'right'
          root.update({ assistantDock: 'minimized' })
        }
        return
      }
      if (!open && wasOpen && sc.voiceDockRestore) {
        const dock = sc.voiceDockRestore
        sc.voiceDockRestore = null
        // Dock only — assistantOpen is never touched, so a drawer the user
        // closed outright mid-session stays closed (the dock value is inert
        // while closed).
        root.update({ assistantDock: dock })
      }
    },

    // ——— voice engine plumbing (FA204/FA207 frank trap) ———
    //
    // Every one of these used to be a module-scope `const _name = (overlay,
    // ...) => {...}` split across functions/voiceSession.js AND
    // functions/voiceConvai.js — both files independently declared their own
    // `_overlayOf`/`_rootOf`/`_voiceOf`/`_up` (the exact same-name collision
    // frank's own esbuild-dedup rename warned about, `_rootOf2`/`_cache2`
    // style), and every sibling-helper bare call inside them (`_teardown`
    // calling `_stopConvai`, `_speak` calling `_play`, etc.) was a frank trap
    // the moment those functions/*.js files got frank.toJSON'd for platform
    // delivery: each exported `el.call` handler is serialized independently,
    // so a free reference to a module-scope sibling reads as `undefined`
    // post-serialization (project_frank_drops_module_local_helpers).
    //
    // Fix: this whole engine is genuinely ONE piece of state machinery keyed
    // off a single VoiceOverlay instance (`voiceSession.js`'s own header:
    // "every runtime handle lives on the VoiceOverlay element's scope") — so
    // it belongs in THIS component's `scope: {}`, the same construct
    // `syncFocus`/`syncChatSuppression` above already use, and the same one
    // AppOrgSwitcher.js's `rawOrgs`/`activeOrg` pair uses for cross-calling
    // scope methods (`this.rawOrgs(root)`). `scope: {}` is confirmed to
    // survive frank serialization as ONE co-located blob (live Chrome MCP
    // verification, tickets/sonnet.md 2026-08-12 addendum: `el.scope.foo`
    // resolves directly in the served platform payload). voiceSession.js and
    // voiceConvai.js now call these via `overlay.scope.<name>(...)`; every
    // cross-method call inside this block uses `this.<name>(...)`.
    //
    // `_overlayOf` itself is NOT here — it is how callers GET to
    // `overlay.scope` in the first place, so it can't live on the thing it
    // resolves. It stays a tiny (4-line) self-contained local, duplicated in
    // every `el.call` export in voiceSession.js/voiceConvai.js (FA207).
    // `_resume` (voiceSession.js line 272, an unused re-arm-listening helper
    // with zero call sites anywhere in the file — confirmed via the acorn
    // AST sweep and a plain grep) was dead code and is not migrated.

    rootOf(overlay) {
      const st = overlay && overlay.state
      return (st && st.root) || st
    },

    voiceOf(overlay) {
      const sc = overlay.scope
      if (!sc.voice) sc.voice = {}
      return sc.voice
    },

    // THREE surfaces render voice state (full VoiceOverlay, in-composer
    // VoiceStrip, the unified island in SimoneReasoningPill), so every write
    // goes UNSCOPED and repaints them all. The server engines emit per-turn
    // transitions only — the several-per-second interim tick that once
    // earned a scoped update is gone with the browser recognizer.
    up(overlay, patch) {
      const root = this.rootOf(overlay)
      if (!root || !root.update) return
      root.update(patch, { preventFetch: true })
    },

    // THE canonical transcript primitive — inline copy of the exported
    // `appendVoiceTurn` (voiceSession.js; kept exported for its own unit
    // test, `voiceSession.test.js`, per FA207/pattern-4: a same-file bare
    // call from a private helper is the same frank trap as a sibling-export
    // call, so the logic is duplicated here rather than referenced bare).
    // Upserts by `id`: a streaming agent turn keeps the same id and is
    // rewritten as its text grows, which is what preserves token-by-token
    // streaming through a state-backed log.
    turn(overlay, entry) {
      const root = this.rootOf(overlay)
      if (!root || !entry || !entry.id) return
      const list = Array.isArray(root.voiceTurns) ? root.voiceTurns : []
      const max = 50
      const at = list.findIndex((t) => t && t.id === entry.id)
      const next = at === -1 ? list.concat([entry]) : list.slice()
      if (at !== -1) next[at] = { ...next[at], ...entry }
      const turns = next.length > max ? next.slice(next.length - max) : next
      this.up(overlay, { voiceTurns: turns })
    },

    // An executed canvas command, logged as text into the one thread. `kind:
    // 'action'` lets the UI render an outcome differently from speech without
    // knowing which engine ran (2026-08-03 per Nika "in command mode it
    // should also text the answers").
    actionTurn(overlay, label) {
      const text = String(label || '').trim()
      if (!text) return
      const v = this.voiceOf(overlay)
      v.turnSeq = (v.turnSeq || 0) + 1
      this.turn(overlay, { id: 'a' + v.turnSeq, role: 'agent', kind: 'action', text })
    },

    // Mirror the scope-held engine handle into reactive state. Both must move
    // together: `v.engine` drives dispatch, `root.voiceEngine` lets the UI
    // say which engine is live.
    setEngine(overlay, name) {
      const v = this.voiceOf(overlay)
      v.engine = name
      this.up(overlay, { voiceEngine: name })
    },

    // `key` is a bundle key, resolved HERE — `voiceError` is written into root
    // state and read back by the status line below, so it has to be a string by
    // the time it lands (I18N-CONVENTION: data handed to a state carrier
    // resolves eagerly at the call site). `detail` is server prose appended
    // verbatim behind the translated headline.
    fail(overlay, key, detail) {
      const v = this.voiceOf(overlay)
      this.stopRecorder(v)
      // `|| key` is the SAFETY half, not a fallback for a missing translation:
      // `translate()` already returns the key itself on a miss, so this only
      // fires when polyglot is not registered at all (a bare stub, a surface
      // booted without the plugin). Without it an unregistered resolver turns
      // every voice error into the literal string 'undefined'. Server prose
      // arriving here instead of a key passes straight through either way.
      const headline = overlay.call('polyglot', key) || key
      this.up(overlay, {
        voiceStatus: 'error',
        voiceError: headline + (detail ? ' — ' + detail : '')
      })
    },

    // The 503 path — the server names the missing env var and the operator
    // step. Surface it verbatim behind the headline (VoiceService docs: the
    // message is the difference between "voice is broken" and "voice isn't
    // provisioned"), stripping the server's own headline so the copy doesn't
    // stutter.
    notProvisioned(overlay, err) {
      const detail = String((err && err.message) || '').replace(
        /^Voice \w+ is unavailable:\s*/i,
        ''
      )
      this.fail(overlay, 'shell.voice.error.notProvisioned', detail)
    },

    stopTracks(v) {
      if (v.stream) {
        v.stream.getTracks().forEach((t) => t.stop())
        v.stream = null
      }
      if (v.audioCtx) {
        try {
          v.audioCtx.close()
        } catch (_) {}
        v.audioCtx = null
      }
      // The live agent installs an analyser-shaped adapter over its client's
      // spectra (`startAgentEngine`) — releasing OUR capture must not tear
      // down THEIR wave source; `stopAgent` releases it.
      if (!(v.analyser && v.analyser.__agent)) {
        v.analyser = null
        v.bins = null
      }
    },

    // Chat state keys are namespaced per surface — same map as sendPrompt.js.
    // Shared across voiceSession.js's `commitAssistant` (below) AND the
    // exported `voiceStart` (which needs it before a session even opens).
    chatPrefix(scope) {
      return scope === 'addapp' ? '_ax_' : scope === 'dashboard' ? '_db_' : ''
    },

    // The SDK instance — same accessor chain the shell uses (app.js:
    // `el.sdk || el.context?.methods?.sdk`), so the engine works on every
    // mount.
    sdkOf(overlay) {
      return (
        overlay.sdk ||
        (overlay.context && overlay.context.methods && overlay.context.methods.sdk) ||
        null
      )
    },

    async mic(overlay) {
      const v = this.voiceOf(overlay)
      if (v.stream) return
      const md = typeof navigator !== 'undefined' && navigator.mediaDevices
      if (!md || !md.getUserMedia) {
        this.fail(overlay, 'shell.voice.error.micUnsupported')
        return
      }
      try {
        // echoCancellation keeps the recorder from capturing the spoken reply
        // out of the speakers; AGC keeps quiet speech above the silence
        // threshold.
        const stream = await md.getUserMedia({
          audio: { echoCancellation: true, noiseSuppression: true, autoGainControl: true }
        })
        if (!overlay.node || !v.active) {
          stream.getTracks().forEach((t) => t.stop())
          return
        }
        const Ctx = window.AudioContext || window.webkitAudioContext
        const audioCtx = new Ctx()
        const analyser = audioCtx.createAnalyser()
        analyser.fftSize = 128
        analyser.smoothingTimeConstant = 0.72
        audioCtx.createMediaStreamSource(stream).connect(analyser)
        v.stream = stream
        v.audioCtx = audioCtx
        v.analyser = analyser
        v.bins = new Uint8Array(analyser.frequencyBinCount)
      } catch (err) {
        this.fail(
          overlay,
          err && (err.name === 'NotAllowedError' || err.name === 'SecurityError')
            ? 'shell.voice.error.micDenied'
            : 'shell.voice.error.micOpenFailed'
        )
      }
    },

    flashDone(overlay, label) {
      const v = this.voiceOf(overlay)
      this.up(overlay, { voiceStatus: 'done', voiceResult: label || 'Done' })
      clearTimeout(v.doneTimer)
      v.doneTimer = setTimeout(() => {
        if (!overlay.node || !v.active) return
        // A command is a one-off: it executed, the answer is in the thread,
        // the ✓ has been shown — get out of the user's way (2026-08-02 per
        // Nika). The SIMONE talk loop goes back to listening instead — a
        // conversation ends only on ✕/Esc.
        if (v.engine === 'simone') this.relisten(overlay)
        else this.teardown(overlay)
      }, 1100)
    },

    // ——— STT engine: MediaRecorder → sdk.voiceTranscribe ———

    record(overlay) {
      const v = this.voiceOf(overlay)
      if (!v.stream || v.recorder) return
      const MR = typeof window !== 'undefined' && window.MediaRecorder
      if (!MR) {
        this.fail(overlay, 'shell.voice.error.recorderUnsupported')
        return
      }
      let mime = ''
      const candidates = ['audio/webm;codecs=opus', 'audio/webm', 'audio/mp4']
      for (const c of candidates) {
        if (MR.isTypeSupported && MR.isTypeSupported(c)) {
          mime = c
          break
        }
      }
      let recorder
      try {
        recorder = mime ? new MR(v.stream, { mimeType: mime }) : new MR(v.stream)
      } catch (_) {
        this.fail(overlay, 'shell.voice.error.recorderStartFailed')
        return
      }
      v.chunks = []
      recorder.ondataavailable = (ev) => {
        if (ev.data && ev.data.size) v.chunks.push(ev.data)
      }
      recorder.onstop = () => {
        if (v.recorder === recorder) this.transcribe(overlay)
      }
      v.recorder = recorder
      v.heardSpeech = false
      v.lastSpeechAt = 0
      v.recordStartedAt = Date.now()
      try {
        recorder.start()
      } catch (_) {
        v.recorder = null
        this.fail(overlay, 'shell.voice.error.recorderStartFailed')
        return
      }
      clearInterval(v.silenceLoop)
      v.silenceLoop = setInterval(() => this.watchSilence(overlay), 150)
    },

    // End-of-utterance detection off the SAME analyser the waveform reads:
    // mark speech while the level is above threshold, commit after a silence
    // gap, and hard-stop at the cap. Whisper has no interim results, so this
    // watcher is what turns an open mic into a one-shot utterance.
    watchSilence(overlay) {
      // Average analyser level (0..1) above which the frame counts as
      // speech. AGC (requested in `mic`) normalises quiet mics toward this
      // band.
      const SPEECH_LEVEL = 0.05
      // Silence gap after heard speech that commits the utterance.
      const SILENCE_COMMIT_MS = 1500
      // Hard cap per utterance — a stuck session must not record forever.
      const MAX_UTTERANCE_MS = 45000
      const v = this.voiceOf(overlay)
      const root = this.rootOf(overlay)
      if (!v.recorder || v.recorder.state !== 'recording') return
      if (root && root.voiceMicMuted) return
      let level = 0
      if (v.analyser && v.bins) {
        v.analyser.getByteFrequencyData(v.bins)
        let sum = 0
        for (let i = 0; i < v.bins.length; i++) sum += v.bins[i]
        level = sum / (v.bins.length * 255)
      }
      const now = Date.now()
      if (level > SPEECH_LEVEL) {
        v.heardSpeech = true
        v.lastSpeechAt = now
      }
      const tooLong = now - v.recordStartedAt > MAX_UTTERANCE_MS
      const silent = v.heardSpeech && v.lastSpeechAt && now - v.lastSpeechAt > SILENCE_COMMIT_MS
      if (tooLong || silent) this.finishRecording(overlay)
    },

    // Stop capturing and let the recorder's onstop hand the clip to
    // `transcribe`.
    finishRecording(overlay) {
      const v = this.voiceOf(overlay)
      clearInterval(v.silenceLoop)
      v.silenceLoop = null
      const rec = v.recorder
      if (!rec) return
      this.up(overlay, { voiceStatus: 'thinking' })
      try {
        if (rec.state !== 'inactive') rec.stop()
        else if (v.recorder === rec) this.transcribe(overlay)
      } catch (_) {
        v.recorder = null
      }
    },

    // Release the recorder WITHOUT transcribing (teardown, mode switch,
    // failure).
    stopRecorder(v) {
      clearInterval(v.silenceLoop)
      v.silenceLoop = null
      v.sttToken = null
      const rec = v.recorder
      v.recorder = null
      v.chunks = null
      if (rec) {
        rec.ondataavailable = null
        rec.onstop = null
        try {
          if (rec.state !== 'inactive') rec.stop()
        } catch (_) {}
      }
    },

    async transcribe(overlay) {
      const v = this.voiceOf(overlay)
      const rec = v.recorder
      v.recorder = null
      const chunks = v.chunks || []
      v.chunks = null
      if (!v.active) return
      const type = (rec && rec.mimeType) || 'audio/webm'
      const blob = new Blob(chunks, { type: type.split(';')[0] })
      // A clip in which the live level never crossed the speech threshold is
      // NOT sent. Whisper turns silence and room noise into stock phrases —
      // one second of digital silence came back as " Thank you." on dev
      // 2026-10-04 — so the 45 s cap in a quiet room (or a hold released
      // without a word) handed the assistant a turn nobody said, and the
      // answer was spoken and billed. Judged only while the level is live
      // (analyser + bins, context running): without a level to read, the clip
      // is sent as before rather than dropped unheard.
      const levelLive = !!(
        v.analyser &&
        v.bins &&
        (!v.audioCtx || !v.audioCtx.state || v.audioCtx.state === 'running')
      )
      if (!blob.size || (levelLive && !v.heardSpeech)) {
        if (v.closeAfterCommit) this.teardown(overlay)
        else if (v.engine === 'simone') this.relisten(overlay)
        else this.fail(overlay, 'shell.voice.error.noSpeech')
        return
      }
      const sdk = this.sdkOf(overlay)
      if (!sdk || typeof sdk.voiceTranscribe !== 'function') {
        this.fail(overlay, 'shell.voice.error.unavailableHere')
        return
      }
      const recExt = (mime) => {
        const m = String(mime || '').toLowerCase()
        if (m.includes('mp4')) return 'mp4'
        if (m.includes('ogg')) return 'ogg'
        return 'webm'
      }
      const token = {}
      v.sttToken = token
      let out = null
      try {
        // The speaker's UI language is Whisper's hint — without it a
        // Georgian clip came back as Latin transliteration (dev 2026-10-07).
        // The server keeps only its own languages.
        const root = this.rootOf(overlay)
        const lang = root && typeof root.lang === 'string' && root.lang ? root.lang : null
        const args = { audio: blob, filename: 'clip.' + recExt(type) }
        if (lang) args.language = lang
        out = await sdk.voiceTranscribe(args)
      } catch (err) {
        if (v.sttToken !== token || !v.active) return
        if (err && err.status === 503) this.notProvisioned(overlay, err)
        else this.fail(overlay, 'shell.voice.error.transcribeFailed')
        return
      }
      if (v.sttToken !== token || !v.active) return
      const text = String((out && out.text) || '').trim()
      // Whisper renders pure silence as '' or a lone punctuation mark.
      if (!text || /^[.,!?\s]+$/.test(text)) {
        if (v.closeAfterCommit) this.teardown(overlay)
        // A continuous conversation hears silence and noise all the time —
        // an unclear clip just re-arms the mic; an error state here would
        // stop the loop for nothing.
        else if (v.engine === 'simone') this.relisten(overlay)
        else this.fail(overlay, 'shell.voice.error.noSpeech')
        return
      }
      this.commit(overlay, text)
    },

    // ——— TTS: sdk.voiceTts → one primed Audio element ———

    // One reusable Audio element, created INSIDE the user gesture that starts
    // a session. Autoplay policy ties playback to user activation and the
    // reply plays seconds after the click — an element that already played (a
    // silent clip, paused immediately) keeps its permission for every later
    // src swap. Replaces v1's speechSynthesis priming.
    primeAudio(overlay) {
      // A minimal valid silent WAV (44-byte header + 4 silent samples) used
      // to unlock the reply element inside the user gesture here.
      const SILENT_WAV =
        'data:audio/wav;base64,UklGRiwAAABXQVZFZm10IBAAAAABAAEAQB8AAIA+AAACABAAZGF0YQgAAAAAAAAAAAAAAA=='
      const v = this.voiceOf(overlay)
      if (v.audioEl) return
      if (typeof window === 'undefined' || typeof window.Audio !== 'function') return
      try {
        const audio = new window.Audio(SILENT_WAV)
        audio.playsInline = true
        const p = audio.play()
        if (p && typeof p.catch === 'function') p.catch(() => {})
        try {
          audio.pause()
        } catch (_) {}
        v.audioEl = audio
      } catch (_) {}
    },

    // Cut the current spoken reply short — clearing the token first makes
    // every in-flight callback and the streaming pump abandon themselves;
    // aborting the controller cancels the SDK fetch (the server aborts
    // upstream on disconnect).
    stopSpeaking(overlay) {
      const v = this.voiceOf(overlay)
      v.speakToken = null
      clearTimeout(v.speakGuard)
      v.speakGuard = null
      if (v.speakAbort) {
        try {
          v.speakAbort.abort()
        } catch (_) {}
        v.speakAbort = null
      }
      if (v.audioEl) {
        try {
          v.audioEl.pause()
        } catch (_) {}
      }
      if (v.speakUrl) {
        try {
          URL.revokeObjectURL(v.speakUrl)
        } catch (_) {}
        v.speakUrl = null
      }
    },

    // Play a streaming TTS Response. MediaSource lets playback start while
    // bytes are still arriving (the whole point of the server streaming the
    // clip); where the mime isn't MSE-supported it falls back to blob +
    // object URL — still correct, just first-audio waits for the download.
    // Resolves when playback ends, errors, or is paused (the stop path
    // pauses).
    play(overlay, res, token) {
      const self = this
      return new Promise((resolve) => {
        const v = self.voiceOf(overlay)
        if (!v.audioEl) v.audioEl = new window.Audio()
        const audio = v.audioEl
        let settled = false
        const finish = () => {
          if (settled) return
          settled = true
          audio.onended = null
          audio.onerror = null
          audio.onpause = null
          resolve()
        }
        audio.onended = finish
        audio.onerror = finish
        audio.onpause = finish
        const type = String((res.headers && res.headers.get('content-type')) || 'audio/mpeg')
          .split(';')[0]
          .trim()
        const MS = typeof window !== 'undefined' && window.MediaSource
        const canStream =
          MS && MS.isTypeSupported && MS.isTypeSupported(type) && res.body && res.body.getReader
        if (canStream) {
          const ms = new MS()
          if (v.speakUrl) {
            try {
              URL.revokeObjectURL(v.speakUrl)
            } catch (_) {}
          }
          v.speakUrl = URL.createObjectURL(ms)
          audio.src = v.speakUrl
          ms.addEventListener(
            'sourceopen',
            async () => {
              let sb
              try {
                sb = ms.addSourceBuffer(type)
              } catch (_) {
                finish()
                return
              }
              const reader = res.body.getReader()
              const append = (chunk) =>
                new Promise((ok) => {
                  sb.addEventListener('updateend', ok, { once: true })
                  try {
                    sb.appendBuffer(chunk)
                  } catch (_) {
                    ok()
                  }
                })
              try {
                for (;;) {
                  const { done, value } = await reader.read()
                  if (v.speakToken !== token) {
                    try {
                      reader.cancel()
                    } catch (_) {}
                    break
                  }
                  if (done) break
                  await append(value)
                }
              } catch (_) {}
              try {
                if (ms.readyState === 'open') ms.endOfStream()
              } catch (_) {}
            },
            { once: true }
          )
          const p = audio.play()
          if (p && typeof p.catch === 'function') p.catch(finish)
        } else {
          res
            .blob()
            .then((b) => {
              if (v.speakToken !== token) {
                finish()
                return
              }
              if (v.speakUrl) {
                try {
                  URL.revokeObjectURL(v.speakUrl)
                } catch (_) {}
              }
              v.speakUrl = URL.createObjectURL(b)
              audio.src = v.speakUrl
              const p = audio.play()
              if (p && typeof p.catch === 'function') p.catch(finish)
            })
            .catch(finish)
        }
      })
    },

    // The workspace's own ElevenLabs voice for the spoken reply —
    // `settings.voice.voiceId`, an id (not a secret) the workspace chose.
    // Without it every workspace spoke in the server's default voice: the
    // SDK and the TTS route carry `voiceId`, only this call dropped it.
    // DECISION site (workspaceSettings.js §5): "no voice set" and "record not
    // loaded yet" both fall to the server default, which is the right answer
    // for both. Shape-checked so a typo never reaches the TTS URL.
    ttsVoiceOf(overlay) {
      // THE workspace voice reader (functions/workspaceVoice.js) — the same
      // one speakReply uses, so the overlay and typed answers never differ.
      const fns = (overlay && overlay.context && overlay.context.functions) || {}
      if (typeof fns.workspaceVoiceId !== 'function') return null
      return overlay.call('workspaceVoiceId', this.rootOf(overlay)) || null
    },

    async speak(overlay, text) {
      const v = this.voiceOf(overlay)
      if (!text) {
        this.afterSpeak(overlay)
        return
      }
      const sdk = this.sdkOf(overlay)
      if (!sdk || typeof sdk.voiceTts !== 'function') {
        this.afterSpeak(overlay)
        return
      }
      this.up(overlay, { voiceStatus: 'speaking' })
      // The talk loop hears the person over its own reply (the barge-in).
      this.startBargeWatch(overlay)
      const token = {}
      v.speakToken = token
      const ctrl = typeof AbortController === 'function' ? new AbortController() : null
      v.speakAbort = ctrl
      // Hard cap so a stalled stream can never wedge the session in
      // 'speaking' — generous (~11 chars/s + slack); the normal end path
      // clears it first.
      clearTimeout(v.speakGuard)
      v.speakGuard = setTimeout(
        () => {
          if (v.speakToken !== token) return
          this.stopSpeaking(overlay)
          this.afterSpeak(overlay)
        },
        Math.min(120000, 8000 + text.length * 90)
      )
      const ttsArgs = { text, signal: ctrl && ctrl.signal }
      const voiceId = this.ttsVoiceOf(overlay)
      if (voiceId) ttsArgs.voiceId = voiceId
      try {
        const res = await sdk.voiceTts(ttsArgs)
        if (v.speakToken !== token || !v.active) return
        await this.play(overlay, res, token)
      } catch (err) {
        if (v.speakToken !== token || !v.active) return
        this.stopSpeaking(overlay)
        if (err && err.status === 503) {
          // Secrets unprovisioned — say so and stop. The text answer already
          // stands in the thread; never fall back to a browser voice.
          this.notProvisioned(overlay, err)
          return
        }
        // Transient TTS failure: the reply is on screen — finish the
        // exchange silently rather than erroring a delivered answer.
        this.afterSpeak(overlay)
        return
      }
      if (v.speakToken !== token || !v.active) return
      this.stopSpeaking(overlay)
      this.afterSpeak(overlay)
    },

    afterSpeak(overlay) {
      const v = this.voiceOf(overlay)
      if (!overlay.node || !v.active) return
      // A notice said MID-turn (an ask waits on the person — holdForAsk) is
      // not the turn's end: the turn is still out, so the loop holds for the
      // answer instead of listening.
      if (v.awaiting && v.awaiting.notice) {
        this.noticeSaid(overlay)
        return
      }
      // The spoken reply is the END of a one-off command exchange, and the
      // END OF ONE TURN of the SIMONE talk loop — which goes straight back to
      // listening (speak → listen is the loop's closing edge).
      if (v.engine === 'simone') this.relisten(overlay)
      else this.teardown(overlay)
    },

    // ——— SIMONE talk loop: the listen edge ———

    // Re-arm the mic for the next utterance of a continuous conversation
    // (VOICE-TALK-ONLY-SIMONE-1 part 3, route A). Reached after a spoken
    // reply, a stop-speaking barge-in, an unclear clip, or a ✓ action. The
    // mic stream + analyser survive across turns (teardown is the only
    // release), so this is a plain `record`; a stream lost mid-session (device
    // unplugged, permission revoked) re-runs the engine start. A user-muted
    // mic stays muted — voiceMicToggle's unmute re-arms via `record`.
    relisten(overlay) {
      const v = this.voiceOf(overlay)
      if (!overlay.node || !v.active) return
      v.awaiting = null
      clearInterval(v.turnPoll)
      v.turnPoll = null
      clearTimeout(v.stopTimer)
      this.stopBargeWatch(v)
      this.up(overlay, {
        voiceStatus: 'listening',
        voiceInterim: '',
        voiceResult: '',
        voiceError: ''
      })
      const root = this.rootOf(overlay)
      if (root && root.voiceMicMuted) return
      if (!v.stream) {
        this.startRecorderEngine(overlay, 'simone')
        return
      }
      if (!v.recorder) this.record(overlay)
    },

    // ——— SIMONE talk loop: the barge-in ———

    // The person speaks over the agent (Nika 2026-10-23: "fly voice and ai
    // should stop when I talk, add interrupting"). While a turn is out — the
    // ask dispatched and the agent thinking (`awaiting`), or its reply playing
    // (`speaking`) — the SAME analyser the waveform reads keeps listening
    // (the mic stream lives for the whole session). Sustained speech over it
    // is an interrupt: the voice stops, the turn being made stops (the host's
    // own stop — the shell's Fly ends the page in the making, its chat ends
    // the reply being written), a late answer
    // is never spoken, and the new utterance records at once. The bar while
    // the reply plays is higher than while the agent thinks: the echo
    // canceller (`mic` asks for echoCancellation) takes the reply out of the
    // mic, and what it leaves must not count as the person. A muted mic never
    // interrupts. The talk loop only; a one-shot command just finishes.
    startBargeWatch(overlay) {
      const v = this.voiceOf(overlay)
      this.stopBargeWatch(v)
      // No level to read (the mic never opened): nothing to hear the person by.
      if (!overlay.node || !v.active || v.engine !== 'simone' || !v.analyser || !v.bins) return
      // A turn whose ask waits on the person is never cut by speech: the stop
      // would decline a parked step and strand a question's card. The person
      // answers it on the screen.
      if (v.awaiting && v.awaiting.waitStep) return
      v.bargeLoop = setInterval(() => this.watchBarge(overlay), 100)
    },

    stopBargeWatch(v) {
      clearInterval(v.bargeLoop)
      v.bargeLoop = null
      v.bargeFrames = 0
    },

    watchBarge(overlay) {
      // Average analyser level (watchSilence's own measure) that counts as
      // the person speaking: over a reply that plays, and over a turn being
      // made (the room is quiet then — the agent is not speaking).
      const BARGE_LEVEL_SPEAKING = 0.08
      const BARGE_LEVEL_THINKING = 0.05
      // Consecutive 100 ms frames above the bar: speech, not a cough or a
      // click.
      const BARGE_FRAMES = 3
      const v = this.voiceOf(overlay)
      const root = this.rootOf(overlay)
      const status = root && root.voiceStatus
      const out = status === 'speaking' || (status === 'thinking' && !!v.awaiting)
      // An ask that waits on the person holds the turn (startBargeWatch).
      const held = !!(v.awaiting && v.awaiting.waitStep)
      if (
        !overlay.node ||
        !v.active ||
        v.engine !== 'simone' ||
        !out ||
        held ||
        !v.analyser ||
        !v.bins
      ) {
        this.stopBargeWatch(v)
        return
      }
      if (root.voiceMicMuted || v.recorder) {
        v.bargeFrames = 0
        return
      }
      v.analyser.getByteFrequencyData(v.bins)
      let sum = 0
      for (let i = 0; i < v.bins.length; i++) sum += v.bins[i]
      const level = sum / (v.bins.length * 255)
      const bar = status === 'speaking' ? BARGE_LEVEL_SPEAKING : BARGE_LEVEL_THINKING
      v.bargeFrames = level > bar ? (v.bargeFrames || 0) + 1 : 0
      if (v.bargeFrames >= BARGE_FRAMES) this.barge(overlay)
    },

    barge(overlay) {
      const v = this.voiceOf(overlay)
      const root = this.rootOf(overlay)
      this.stopBargeWatch(v)
      if (!overlay.node || !v.active || !root) return
      const turn = v.awaiting
      v.awaiting = null
      this.stopSpeaking(overlay)
      // The turn being made stops with the voice — the host's own stop: the
      // Fly page in the making, or the chat reply being written (its scope's
      // turn; the shell's assistantTurnStop).
      const fns = (overlay.context && overlay.context.functions) || {}
      if (turn && turn.type === 'fly' && typeof fns.assistantFlyStop === 'function') {
        try {
          overlay.call('assistantFlyStop')
        } catch (_) {}
      }
      if (turn && turn.type === 'chat' && typeof fns.assistantTurnStop === 'function') {
        try {
          overlay.call('assistantTurnStop', v.chatScope || 'general')
        } catch (_) {}
      }
      // A reply that was streaming into the log stops where it was cut.
      const id = turn && turn.turnId
      const list = Array.isArray(root.voiceTurns) ? root.voiceTurns : []
      if (id && list.some((t) => t && t.id === id)) this.turn(overlay, { id, streaming: false })
      // Listen at once: the person is already speaking — the clip counts as
      // speech from its first frame.
      this.relisten(overlay)
      if (v.recorder) {
        v.heardSpeech = true
        v.lastSpeechAt = Date.now()
      }
    },

    // ——— what waits on the person: a step for the OK, a question for a pick ———
    //
    // Two kinds of ask stop a turn on the person, and the server turn waits
    // on that tool's result, so Simone says NOTHING until the person answers:
    //   • an approval — the client gate parks a write the turn wants to make
    //     (simoneAiTransport.runClientToolWithAuth): its tool row turns
    //     `status: 'approval'`; Run or Skip answers it;
    //   • a question — ask_user hangs a confirm/choice card on the message
    //     (simoneAiTransport askUser); the card's buttons answer it
    //     (resolveChatCard).
    // The typing flag tells nothing: a live parked step keeps it on, a step
    // whose follow window ended has it off, a question turns it off — each
    // time with the narration as the newest text. So the ask is read off the
    // thread (voiceOnStateChange → heldAsk), the call screen shows it (AiAsk),
    // and the voice says it.

    // The ask of this turn that waits on the person, or null — the newest
    // among the assistant rows after the person's last line (a new line
    // closes both kinds: simoneAiTransport.supersedeParkedAiToolApprovals):
    //   { kind: 'approval', id, label, tool } — a tool row in the `approval`
    //     status (`label`: the server's words for the step);
    //   { kind: 'choice', id, prompt, options } — a confirm/choice card nobody
    //     answered (`options`: its button labels, in order, in the words the
    //     screen shows — the ONE rule the card and the call screen read,
    //     functions/askCardOptions, through the overlay: a confirm with no
    //     labels of its own is Confirm / Cancel in the page language).
    // Mirrors AiAsk.scope.waitingIn (the call screen and the island show the
    // same ask).
    heldAsk(rows, overlay) {
      const list = Array.isArray(rows) ? rows : []
      for (let i = list.length - 1; i >= 0; i--) {
        const m = list[i]
        if (!m) continue
        if (m.role === 'user') return null
        if (m.role !== 'assistant') continue
        const tools = Array.isArray(m.tools) ? m.tools : []
        for (let j = tools.length - 1; j >= 0; j--) {
          const t = tools[j]
          if (t && t.status === 'approval') {
            return { kind: 'approval', id: t.id, label: t.label, tool: t.tool }
          }
        }
        const cards = Array.isArray(m.cards) ? m.cards : []
        for (let j = cards.length - 1; j >= 0; j--) {
          const c = cards[j]
          const asks = !!c && (c.type === 'choice' || c.type === 'confirm')
          if (!asks || !c._cardId || c._resolved) continue
          let answers = null
          try {
            answers = overlay && overlay.call('askCardOptions', c)
          } catch (_) {
            answers = null
          }
          const options = (Array.isArray(answers) ? answers : [])
            .map((o) => String(o.label))
            .filter(Boolean)
          return {
            kind: 'choice',
            id: 'card:' + c._cardId,
            prompt: typeof c.prompt === 'string' ? c.prompt.trim() : '',
            options
          }
        }
      }
      return null
    },

    // What the voice says while an ask waits, in the person's language. A
    // step: in its own words (the server's label, already in the turn's
    // language — the words the call screen shows), and that Run or Skip on
    // the screen answers it. A question: the question itself (the model's
    // words, in the turn's language), and that the answer is chosen on the
    // screen. An ask with no words of its own: the line without them.
    askNotice(overlay, ask) {
      const t = (key) => {
        let raw = ''
        try {
          raw = overlay.call('polyglot', key)
        } catch (_) {
          raw = ''
        }
        return typeof raw === 'string' && raw !== key ? raw : ''
      }
      if (ask && ask.kind === 'choice') {
        const prompt = ask.prompt || ''
        // A question that ends without a stop gets one, so the line after it
        // is a sentence of its own.
        const said = prompt && !/[.?!…:;]$/.test(prompt) ? prompt + '.' : prompt
        const line = said ? t('ai.voice.choiceNotice') : ''
        if (line && line.includes('{prompt}')) return line.split('{prompt}').join(said)
        return t('ai.voice.choiceNoticeBare')
      }
      const label = ask && typeof ask.label === 'string' ? ask.label.trim() : ''
      const line = label ? t('ai.voice.askNotice') : ''
      if (line && line.includes('{step}')) return line.split('{step}').join(label)
      return t('ai.voice.askNoticeBare')
    },

    // The loop (or the one-shot command) reached an ask that waits. The voice
    // says so ONCE per ask; then the loop HOLDS — 'waiting' for a step's OK,
    // 'choosing' for a question's pick: no listening (a new utterance is a new
    // send, and a new send closes the ask unanswered —
    // simoneAiTransport.supersedeParkedAiToolApprovals), no barge-in (its stop
    // declines a parked step — settleParkedAiToolApprovals — and strands a
    // question's card), no turn timeout (the time on the screen is the
    // person's). The answer on the screen resumes the turn (askAnswered). The
    // one-shot command says it and ends: the ask stays where the island and
    // the thread still offer it.
    holdForAsk(overlay, ask) {
      const v = this.voiceOf(overlay)
      const turn = v.awaiting
      if (!turn || !ask || turn.waitStep === ask.id) return
      turn.waitStep = ask.id
      turn.waitKind = ask.kind
      this.stopBargeWatch(v)
      const said = this.askNotice(overlay, ask)
      if (v.engine !== 'simone') {
        v.awaiting = null
        clearInterval(v.turnPoll)
        v.turnPoll = null
        this.speak(overlay, said)
        return
      }
      // Set before speaking: a voice that cannot speak ends at once.
      turn.notice = true
      this.speak(overlay, said)
    },

    // The notice ended — said, cut by Stop speaking, or dropped by a failed
    // voice. Hold for the answer, and read the thread again at once: the
    // answer may have come while the notice played (its reply waited, so
    // nothing talked over the notice).
    noticeSaid(overlay) {
      const v = this.voiceOf(overlay)
      const turn = v.awaiting
      if (turn) turn.notice = false
      const choosing = !!turn && turn.waitKind === 'choice'
      this.up(overlay, { voiceStatus: choosing ? 'choosing' : 'waiting' })
      try {
        overlay.call('voiceOnStateChange')
      } catch (_) {}
    },

    // The ask was answered on the screen — Run, Skip, a pick — (or a newer
    // send took it): the turn goes on, on a fresh clock — the time on the
    // screen was the person's, not Simone's. The loop thinks again, with the
    // barge-in back on; the live agent's turn states go on as they were.
    askAnswered(overlay) {
      const v = this.voiceOf(overlay)
      const turn = v.awaiting
      if (!turn || !turn.waitStep) return
      turn.waitStep = null
      turn.waitKind = null
      if (turn.tool) {
        turn.startedAt = Date.now()
        return
      }
      v.turnStartedAt = Date.now()
      const root = this.rootOf(overlay)
      const held = root && (root.voiceStatus === 'waiting' || root.voiceStatus === 'choosing')
      if (held) {
        this.up(overlay, { voiceStatus: 'thinking' })
        this.startBargeWatch(overlay)
      }
    },

    // ——— commit pipeline (command mode) ———

    commit(overlay, text) {
      const v = this.voiceOf(overlay)
      clearTimeout(v.stopTimer)
      this.up(overlay, { voiceFinal: text, voiceInterim: '' })
      // Every committed utterance is a transcript turn — the reply needs its
      // question above it in the one thread.
      v.turnSeq = (v.turnSeq || 0) + 1
      this.turn(overlay, { id: 'u' + v.turnSeq, role: 'user', text })
      // TALK = the SIMONE loop: every utterance is a conversation turn for
      // our agent — Simone is the intelligence (route A). The host command
      // grammar (canvas "zoom to fit" …) stays the one-shot COMMAND path's
      // first responder.
      if (v.engine === 'simone') this.commitAssistant(overlay, text)
      else this.commitCommand(overlay, text)
    },

    commitCommand(overlay, text) {
      const v = this.voiceOf(overlay)
      this.up(overlay, { voiceStatus: 'thinking' })
      const fns = overlay.context && overlay.context.functions
      let res = null
      if (fns && typeof fns.voiceProjectCommand === 'function') {
        res = overlay.call('voiceProjectCommand', text)
      }
      if (res && res.handled) {
        if (res.awaiting === 'freestyle') {
          v.awaiting = { type: 'freestyle' }
          return
        }
        this.actionTurn(overlay, res.label)
        this.flashDone(overlay, res.label)
        return
      }
      // No host command layer (or nothing matched) — let the assistant have
      // it.
      this.commitAssistant(overlay, text)
    },

    commitAssistant(overlay, text) {
      const root = this.rootOf(overlay)
      const v = this.voiceOf(overlay)
      // FLY MODE (workspace docs/FLY_MODE.md): a spoken ask composes a PAGE
      // in the main area, like a typed one, and the page's opening is what
      // the voice says — the talk loop then listens again. Only where the
      // shell's fly engine is registered and the board service answers here.
      const fns = (overlay && overlay.context && overlay.context.functions) || {}
      if (
        root.assistantMode === 'fly' &&
        root._aiBoardsAvailable === true &&
        typeof fns.assistantFlyGenerate === 'function'
      ) {
        const turnId = 'a' + (v.turnSeq || 0)
        v.awaiting = { type: 'fly', turnId }
        v.turnStartedAt = Date.now()
        this.up(overlay, { voiceStatus: 'thinking', voiceMinimized: true, voiceHost: null })
        // Speech over the page in the making stops it (the barge-in).
        this.startBargeWatch(overlay)
        Promise.resolve(overlay.call('assistantFlyGenerate', text)).then(
          (page) => {
            if (!v.active || !v.awaiting || v.awaiting.turnId !== turnId) return
            v.awaiting = null
            if (!page || page.status !== 'ready') {
              const why =
                page && typeof fns.assistantFlyStatusKey === 'function'
                  ? overlay.call('assistantFlyStatusKey', page)
                  : ''
              this.fail(overlay, why || 'workspace.assistantFly.failed')
              return
            }
            const said = String(page.opening || page.title || '').trim()
            if (said) this.turn(overlay, { id: turnId, role: 'agent', text: said })
            this.speak(overlay, said)
          },
          () => {
            if (!v.active || !v.awaiting || v.awaiting.turnId !== turnId) return
            v.awaiting = null
            this.fail(overlay, 'workspace.assistantFly.failed')
          }
        )
        return
      }
      if (root.bridgeStatus !== 'open') {
        this.fail(overlay, 'shell.voice.error.assistantOffline')
        return
      }
      // turnId is the STABLE transcript id for the reply this dispatch will
      // produce — voiceOnStateChange rewrites that one entry as the
      // assistant streams, which is what keeps token-by-token growth visible
      // in a state-backed log instead of one blob at the end.
      // `since`: the thread's length before the send — a step that waits for
      // the person's OK is this turn's only after it (an older parked step is
      // superseded by this very send, maybe a tick later).
      const k = this.chatPrefix(v.chatScope)
      const sent = Array.isArray(root[k + 'chatMessages']) ? root[k + 'chatMessages'].length : 0
      v.awaiting = { type: 'chat', k, turnId: 'a' + (v.turnSeq || 0), since: sent }
      // Turn clock — voiceOnStateChange's timeout guard reads it so a
      // transport that never flips chatTyping back cannot wedge the session
      // in 'thinking' forever.
      v.turnStartedAt = Date.now()
      overlay.call('sendPrompt', text, v.chatScope || 'general')
      // Hand the screen back the moment the turn is dispatched: the surface
      // collapses to the unified island so the reply streams in beside a
      // visible, usable workspace. Nulling voiceHost releases a hosting
      // composer.
      this.up(overlay, { voiceStatus: 'thinking', voiceMinimized: true, voiceHost: null })
      // Speech over the answer being written drops it (the barge-in).
      this.startBargeWatch(overlay)
      // Backstop poll. The turn resolves through voiceOnStateChange (wired to
      // the overlay's onStateUpdate), but a transport that patches the
      // thread with a scoped/quiet update would never reach that hook and
      // the session would wedge in 'thinking' forever. The resolver is
      // idempotent and cheap, so poll it too and let whichever path fires
      // first win.
      clearInterval(v.turnPoll)
      v.turnPoll = setInterval(() => {
        try {
          overlay.call('voiceOnStateChange')
        } catch (_) {}
      }, 500)
    },

    // ——— engine switch ———

    // `name` is the engine the recorder serves: 'recorder' (one-shot COMMAND)
    // or 'simone' (the continuous TALK loop) — same capture, different
    // commit/after-speak edges.
    async startRecorderEngine(overlay, name) {
      const v = this.voiceOf(overlay)
      this.setEngine(overlay, name || 'recorder')
      await this.mic(overlay)
      if (!v.active || !v.stream) return
      const root = this.rootOf(overlay)
      // Engine swaps respect a user-muted mic — unmute re-arms via
      // voiceMicToggle.
      if (root && root.voiceMicMuted) return
      this.record(overlay)
    },

    // ——— TALK: the live voice agent ———
    //
    // Nika 2026-10-26: "workspace voice mode should have voice assistant just
    // like we have on fly … implement … with 11labs voice assistant"; her
    // design answer the same night: "11labs talks, Simone acts". An
    // ElevenLabs agent (the Agents Platform, `@elevenlabs/client` over
    // WebRTC) hears and talks in real time: the greeting, live captions, turn
    // taking, the person talking over it. It holds no workspace data. Every
    // workspace question and action goes to Simone (`ask_workspace` →
    // sendPrompt into the voice thread) and every "show me" to Fly
    // (`show_page` → assistantFlyGenerate): CLIENT TOOLS that run here, with
    // the person's own grants. The agent's prompt and the tool names live on
    // the API server (src/domains/ai-voice/voiceAgentDefinition.js); the two
    // lists change together.
    //
    // The agent is private: a call starts only with a single-use token from
    // `sdk.voiceAgentSession` (POST /core/ai/voice/agent — members only, rate
    // limited, in the workspace's own voice). Where that is missing — an
    // older SDK, a server without the route (404) or without the agent (503),
    // a runtime without the client package — talk is the SIMONE loop above,
    // which every server has.
    //
    // The call's minutes cost workspace credits (Nika 2026-10-26: "chain it
    // to credits system so users pay for it as credits"): the server reserves
    // them before it mints the token and answers 402 when the workspace
    // cannot pay. That is a refusal with its own words, never the Simone loop
    // (its turns need the same credits): the 402 names the limit — the
    // balance, an owner's spend cap, the org pool's share for this workspace
    // — and so do the words. When a limit ends the call before the agent's
    // own length limit, the session says when (`maxSeconds`) and which limit
    // (`maxSecondsReason`), and the call ends there in those words. A
    // microphone the browser already blocks is said BEFORE a token is minted:
    // a token holds the workspace's credits while its call may still start.
    //
    // Engine name 'agent'. Its handles live on `v`: `agent` (the live
    // conversation), `agentToken` (one attempt — every callback checks it, so
    // a late callback of an ended attempt never touches the call),
    // `agentMuted` (the cut Stop speaking makes), `agentShows` (Fly pages
    // still being made for it), `agentPaid` (the end of the paid time).

    // The client package, loaded on first use (FA206 — an npm package is a
    // dynamic import inside a handler; pinned in dependencies.js, the runtime
    // importmap, and in package.json). Its own method, so a test hands in a
    // fake client.
    agentClient() {
      return import('@elevenlabs/client')
    },

    // True when the browser already knows the microphone is blocked here
    // (Permissions API). Unknown or unsupported is false: the call's own mic
    // request then asks.
    async micBlocked() {
      try {
        const perms = typeof navigator !== 'undefined' && navigator.permissions
        if (!perms || typeof perms.query !== 'function') return false
        const status = await perms.query({ name: 'microphone' })
        return !!status && status.state === 'denied'
      } catch (_) {
        return false
      }
    },

    // `opts.plain`: the retry in the agent's own language (English) after a
    // session in the person's language never got going — some agents do not
    // take every language.
    async startAgentEngine(overlay, opts) {
      const v = this.voiceOf(overlay)
      const o = opts || {}
      this.stopAgent(overlay)
      this.setEngine(overlay, 'agent')
      this.up(overlay, { voiceStatus: 'connecting', voiceInterim: '', voiceError: '' })
      const token = {}
      v.agentToken = token
      v.agentHeard = false
      v.agentPlain = !!o.plain
      v.agentLang = null
      const mine = () => v.agentToken === token && !!v.active && !!overlay.node
      const root = this.rootOf(overlay) || {}
      const sdk = this.sdkOf(overlay)
      const ws = root.workspace
      const workspaceId = ws && (ws.id || ws._id) ? String(ws.id || ws._id) : null
      // The SIMONE loop: the same call surface on the server pipeline.
      const toLoop = () => {
        if (!mine()) return
        v.agentToken = null
        this.up(overlay, { voiceStatus: 'listening', voiceEngineReason: 'agent-unavailable' })
        this.startRecorderEngine(overlay, 'simone')
      }
      const blocked = await this.micBlocked()
      if (!mine()) return
      if (blocked) {
        v.agentToken = null
        this.fail(overlay, 'shell.voice.error.micDenied')
        return
      }
      let session = null
      try {
        session = await sdk.voiceAgentSession(workspaceId ? { workspaceId } : {})
      } catch (err) {
        if (!mine()) return
        const status = err && err.status
        if (status === 404 || status === 503) {
          toLoop()
          return
        }
        v.agentToken = null
        this.fail(
          overlay,
          status === 429
            ? 'shell.voice.error.agentBusy'
            : status === 402
              ? this.agentLimitText(err.code, false)
              : 'shell.voice.error.agentStartFailed'
        )
        return
      }
      if (!mine()) return
      if (!session || typeof session.conversationToken !== 'string' || !session.conversationToken) {
        toLoop()
        return
      }
      // The paid time runs from the token: the call it opens is never longer.
      const paid = Number(session.maxSeconds)
      const paidUntil = Number.isFinite(paid) && paid > 0 ? Date.now() + paid * 1000 : 0
      const paidBy = session.maxSecondsReason
      let Conversation = null
      try {
        const mod = await this.agentClient()
        // Named export ONLY: the web build has no default export, and
        // Parcel's production scope hoisting refuses any `default` access on
        // such a module as a hard build error (the retired engine broke every
        // dev deploy on it, 2026-08-02).
        Conversation = mod && mod.Conversation
      } catch (_) {
        Conversation = null
      }
      if (!mine()) return
      if (!Conversation || typeof Conversation.startSession !== 'function') {
        toLoop()
        return
      }

      // Who and where, for the agent's prompt; the greeting in the person's
      // language with their first name. The English retry keeps the agent's
      // own greeting and language.
      const code = String(root.lang || '')
        .toLowerCase()
        .split(/[-_]/)[0]
      const lang = !o.plain && /^[a-z]{2,3}$/.test(code) ? code : 'en'
      let languageName = 'English'
      try {
        languageName = new Intl.DisplayNames(['en'], { type: 'language' }).of(lang) || lang
      } catch (_) {
        languageName = lang
      }
      const user = root.user || {}
      const first = String(user.name || '')
        .trim()
        .split(/\s+/)[0]
      const wsName = String((ws && (ws.displayName || ws.name || ws.slug)) || '').trim()
      const dynamicVariables = { language_name: languageName }
      if (first) dynamicVariables.user_name = first
      if (wsName) dynamicVariables.workspace_name = wsName
      const overrides = {}
      if (!o.plain) {
        const greeting = first
          ? String(overlay.call('polyglot', 'shell.voice.agent.greeting') || '').replace(
              '{name}',
              () => first
            )
          : String(overlay.call('polyglot', 'shell.voice.agent.greetingNoName') || '')
        const agent = {}
        if (greeting && !/^shell\./.test(greeting)) agent.firstMessage = greeting
        if (lang !== 'en') {
          agent.language = lang
          v.agentLang = lang
        }
        if (Object.keys(agent).length) overrides.agent = agent
      }
      if (typeof session.voiceId === 'string' && session.voiceId) {
        overrides.tts = { voiceId: session.voiceId }
      }

      let conv = null
      try {
        conv = await Conversation.startSession({
          conversationToken: session.conversationToken,
          connectionType: session.connectionType === 'websocket' ? 'websocket' : 'webrtc',
          overrides,
          dynamicVariables,
          // A tool call is the agent on the line, as a word from it is (the
          // join watch at the end).
          clientTools: {
            ask_workspace: (params) => {
              if (!mine()) return 'Error: the call has ended.'
              v.agentHeard = true
              return this.agentAsk(overlay, params)
            },
            show_page: (params) => {
              if (!mine()) return { status: 'failed', reason: 'call ended' }
              v.agentHeard = true
              return this.agentShow(overlay, params)
            }
          },
          onModeChange: (m) => {
            if (mine()) this.agentMode(overlay, m && m.mode)
          },
          onMessage: (msg) => {
            if (mine()) this.agentCaption(overlay, msg)
          },
          onAgentResponseCorrection: (c) => {
            if (mine()) this.agentCorrection(overlay, c)
          },
          // Events the client has no callback for arrive here — the live
          // partial transcript of the person among them.
          onDebug: (ev) => {
            if (!mine() || !ev || ev.type !== 'tentative_user_transcript') return
            const said = ev.tentative_user_transcription_event
            const text = String((said && said.user_transcript) || '').trim()
            const r = this.rootOf(overlay)
            if (text && !(r && r.voiceMicMuted)) this.up(overlay, { voiceInterim: text })
          },
          onDisconnect: (details) => {
            // Our own end clears the token first: past this guard the far
            // side ended the call.
            if (v.agentToken === token) this.agentEnded(overlay, details)
          },
          // Non-fatal client errors; a fatal one ends the call (onDisconnect).
          onError: () => {}
        })
      } catch (err) {
        if (!mine()) return
        v.agentToken = null
        if (err && (err.name === 'NotAllowedError' || err.name === 'SecurityError')) {
          this.fail(overlay, 'shell.voice.error.micDenied')
          return
        }
        if (v.agentLang) {
          this.startAgentEngine(overlay, { plain: true })
          return
        }
        this.fail(overlay, 'shell.voice.error.agentStartFailed')
        return
      }
      // Ended, superseded or switched while it connected: end it, never
      // adopt it.
      if (!mine()) {
        try {
          const r = conv && conv.endSession()
          if (r && typeof r.catch === 'function') r.catch(() => {})
        } catch (_) {}
        return
      }
      v.agent = conv
      v.agentMuted = false
      // A mic muted while the call connected stays muted.
      const state = this.rootOf(overlay)
      if (state && state.voiceMicMuted) this.agentMicMuted(overlay, true)
      // The orb's ring and the island read `v.analyser` (voiceCallFrame): an
      // analyser-shaped adapter over the call's own spectra, the louder of
      // the person and the agent per band.
      const bins = 64
      v.analyser = {
        __agent: true,
        frequencyBinCount: bins,
        getByteFrequencyData: (out) => {
          let a = null
          let b = null
          try {
            a = conv.getInputByteFrequencyData()
            b = conv.getOutputByteFrequencyData()
          } catch (_) {}
          const len = (a && a.length) || (b && b.length) || 0
          if (!len) {
            out.fill(0)
            return
          }
          const stride = len / out.length
          for (let i = 0; i < out.length; i++) {
            const from = Math.floor(i * stride)
            const to = Math.max(from + 1, Math.floor((i + 1) * stride))
            let peak = 0
            for (let j = from; j < to && j < len; j++) {
              const x = a ? a[j] : 0
              const y = b ? b[j] : 0
              const m = x > y ? x : y
              if (m > peak) peak = m
            }
            out[i] = peak
          }
        }
      }
      v.bins = new Uint8Array(bins)
      if (state && state.voiceStatus === 'connecting')
        this.up(overlay, { voiceStatus: 'listening' })
      // The agent joins the call from its own side and greets first. One
      // that never does (an outage, a token that waited too long — seen live
      // on dev, 2026-10-26) leaves a silent call that says "listening": no
      // word from it in 15 s is a start that failed (in the person's
      // language: once more in English first, as a drop before a word is).
      clearTimeout(v.agentWatch)
      v.agentWatch = setTimeout(() => {
        v.agentWatch = null
        if (v.agentToken === token && !v.agentHeard) this.agentEnded(overlay, null)
      }, 15000)
      // The workspace's credits pay until `paidUntil`: the call ends there.
      clearTimeout(v.agentPaid)
      v.agentPaid = paidUntil
        ? setTimeout(
            () => {
              v.agentPaid = null
              if (v.agentToken === token) this.agentCreditsEnded(overlay, paidBy)
            },
            Math.max(0, paidUntil - Date.now())
          )
        : null
    },

    // The words for the limit that refuses a call or ends it: the balance,
    // an owner's spend cap, or the org pool's share for this workspace. A
    // refused start names it in the 402's `error` (the SDK's `err.code`), a
    // paid time in the session's `maxSecondsReason`; anything else is the
    // balance, which every older server meant.
    agentLimitText(limit, ended) {
      const cap = limit === 'spend_cap' || limit === 'spend_cap_exceeded'
      const share = limit === 'allocation' || limit === 'allocation_exceeded'
      if (ended) {
        if (cap) return 'shell.voice.error.agentSpendCapEnded'
        if (share) return 'shell.voice.error.agentAllocationEnded'
        return 'shell.voice.error.agentCreditsEnded'
      }
      if (cap) return 'shell.voice.error.agentSpendCap'
      if (share) return 'shell.voice.error.agentAllocation'
      return 'shell.voice.error.agentNoCredits'
    },

    // The paid time ran out: the call ends and the error line says which
    // limit ended it. Try again asks the server again — 402 while nothing is
    // left, a new call once there is room.
    agentCreditsEnded(overlay, limit) {
      const v = this.voiceOf(overlay)
      this.stopAgent(overlay)
      if (!v.active || !overlay.node) return
      this.fail(overlay, this.agentLimitText(limit, true))
    },

    // The agent's turn edges. Speaking: the reply plays (unless the person
    // stopped it — the cut turn still drains on the wire). Listening: the
    // reply ended; while a tool is still out the agent is waiting for its
    // answer, which the call shows as thinking.
    agentMode(overlay, mode) {
      const v = this.voiceOf(overlay)
      if (mode === 'speaking') {
        v.agentHeard = true
        if (!v.agentMuted) this.up(overlay, { voiceStatus: 'speaking' })
        return
      }
      if (mode !== 'listening') return
      this.agentRestoreVolume(v)
      const asking = !!(v.awaiting && v.awaiting.tool && !v.awaiting.tool.answered)
      this.up(overlay, { voiceStatus: asking || v.agentShows > 0 ? 'thinking' : 'listening' })
    },

    // The captions: the person's final words and the agent's reply, both into
    // the one thread (voiceTurns) the call screen, the island and the strip
    // render. A user line with no word in it ("...") is the platform's
    // silence marker (the turn timeout passed with nothing said), never the
    // person's words: it stays out of the thread.
    agentCaption(overlay, msg) {
      const v = this.voiceOf(overlay)
      const text = String((msg && msg.message) || '').trim()
      if (!text) return
      const user = (msg.role || msg.source) === 'user'
      if (user && !/[\p{L}\p{N}]/u.test(text)) return
      v.turnSeq = (v.turnSeq || 0) + 1
      if (user) {
        // The person spoke: a reply they cut is over.
        this.agentRestoreVolume(v)
        this.up(overlay, { voiceFinal: text, voiceInterim: '' })
        this.turn(overlay, { id: 'u' + v.turnSeq, role: 'user', text })
        return
      }
      v.agentHeard = true
      v.agentTurnId = 'a' + v.turnSeq
      this.turn(overlay, { id: v.agentTurnId, role: 'agent', text })
    },

    // The person talked over the reply: the agent's line keeps only what was
    // said aloud.
    agentCorrection(overlay, c) {
      const v = this.voiceOf(overlay)
      const text = String((c && c.corrected_agent_response) || '').trim()
      if (v.agentTurnId && text) this.turn(overlay, { id: v.agentTurnId, text })
    },

    // The far side ended the call. 'agent': the agent hung up (it said
    // goodbye and called end_call, or the call reached its length limit) —
    // the call ends here too. Anything else: the line dropped — say so and
    // keep the call so Try again reconnects. A call that never got going:
    // once more in English if it asked for another language, else the start
    // failed.
    agentEnded(overlay, details) {
      const v = this.voiceOf(overlay)
      const heard = !!v.agentHeard
      const retry = !!v.agentLang && !v.agentPlain
      this.stopAgent(overlay)
      if (!v.active || !overlay.node) return
      if (!heard) {
        if (retry) this.startAgentEngine(overlay, { plain: true })
        else this.fail(overlay, 'shell.voice.error.agentStartFailed')
        return
      }
      if (details && details.reason === 'agent') {
        this.teardown(overlay)
        return
      }
      this.fail(overlay, 'shell.voice.error.agentDisconnected')
    },

    // ask_workspace — the agent's question or action for the workspace,
    // asked of Simone in the voice thread with the person's own grants, in
    // ASSISTANT mode (a text answer the agent can say, even when the
    // person's own mode is Fly — a page is show_page's job). Resolves with
    // the answer (voiceOnStateChange → agentSettle), or after 50 s with
    // "still working" — the agent waits 60 s for a tool at most
    // (voiceAgentDefinition.js); the late answer follows as context.
    agentAsk(overlay, params) {
      const v = this.voiceOf(overlay)
      const root = this.rootOf(overlay)
      const request = String((params && params.request) || '')
        .replace(/\s+/g, ' ')
        .trim()
      if (!request) return Promise.resolve('Error: the request is empty.')
      if (!root || root.bridgeStatus !== 'open') {
        return Promise.resolve('Error: the workspace assistant is offline right now.')
      }
      // One ask at a time: a newer one answers the older as replaced.
      if (v.awaiting && v.awaiting.tool) {
        this.agentSettle(overlay, { error: 'replaced by a newer request' })
      }
      return new Promise((resolve) => {
        const tool = { request, resolve, answered: false, timer: null }
        const k = this.chatPrefix(v.chatScope)
        // `since`: the thread's length before the ask — only a row after it
        // can be this ask (the same words may have been asked before).
        const since = Array.isArray(root[k + 'chatMessages']) ? root[k + 'chatMessages'].length : 0
        v.awaiting = { type: 'chat', k, tool, since, startedAt: Date.now() }
        tool.timer = setTimeout(() => {
          if (tool.answered) return
          tool.answered = true
          resolve('Still working on it. Tell the user the answer comes in a moment.')
        }, 50000)
        try {
          overlay.call('sendPrompt', request, v.chatScope || 'general', undefined, undefined, {
            mode: 'assistant'
          })
        } catch (_) {
          this.agentSettle(overlay, { error: 'the workspace assistant could not take the request' })
          return
        }
        this.up(overlay, { voiceStatus: 'thinking' })
        // The same backstop poll as the loop's chat turn (commitAssistant).
        clearInterval(v.turnPoll)
        v.turnPoll = setInterval(() => {
          try {
            overlay.call('voiceOnStateChange')
          } catch (_) {}
        }, 500)
      })
    },

    // The ask_workspace turn ended (voiceOnStateChange read it off the
    // thread): `out` = { text } or { error }. The answer goes back as the
    // tool's result, as plain text the agent can say; an answer after the
    // tool already said "still working" reaches the agent as context, and it
    // tells the person on its next turn.
    agentSettle(overlay, out) {
      const v = this.voiceOf(overlay)
      const turn = v.awaiting
      if (!turn || !turn.tool) return
      v.awaiting = null
      clearInterval(v.turnPoll)
      v.turnPoll = null
      const tool = turn.tool
      clearTimeout(tool.timer)
      // Markdown → words: no code, no markup, a link is its text; a long
      // answer keeps its first 1800 characters.
      const plain = (text) => {
        const t = String(text || '')
          .replace(/```[\s\S]*?```/g, ' ')
          .replace(/`([^`]*)`/g, '$1')
          .replace(/!\[[^\]]*\]\([^)]*\)/g, ' ')
          .replace(/\[([^\]]*)\]\([^)]*\)/g, '$1')
          .replace(/[#*_>~|]/g, ' ')
          .replace(/\s+/g, ' ')
          .trim()
        return t.length > 1800 ? t.slice(0, 1797) + '…' : t
      }
      const error = out && out.error ? plain(out.error) : ''
      const answer = error ? '' : plain(out && out.text)
      if (!tool.answered) {
        tool.answered = true
        tool.resolve(error ? 'Error: ' + error : answer || 'Done.')
      } else if (v.agent && typeof v.agent.sendContextualUpdate === 'function') {
        try {
          v.agent.sendContextualUpdate(
            'Simone finished the request "' +
              tool.request +
              '". ' +
              (error ? 'It failed: ' + error : 'The answer: ' + answer)
          )
        } catch (_) {}
      }
      const root = this.rootOf(overlay)
      if (root && root.voiceStatus === 'thinking' && !(v.agentShows > 0)) {
        this.up(overlay, { voiceStatus: 'listening' })
      }
    },

    // An ask of this request waits on the person (voiceOnStateChange read it
    // off the thread; the call screen shows it — AiAsk). The agent hears it
    // at once, as the tool's result: not done yet — a step waits for Run or
    // Skip on the screen, or a question (its words and its options) waits for
    // the person's pick there. It cannot answer either, and asking again does
    // not: a new ask is a new send, which closes the ask unanswered. A tool
    // already answered ("still working", or an ask before this one) hears it
    // as context. The request stays open: the reply after the answer on the
    // screen follows as context (agentSettle), on a fresh clock
    // (askAnswered).
    agentWaits(overlay, ask) {
      const v = this.voiceOf(overlay)
      const turn = v.awaiting
      if (!turn || !turn.tool || !ask || turn.waitStep === ask.id) return
      turn.waitStep = ask.id
      turn.waitKind = ask.kind
      const tool = turn.tool
      let text = ''
      if (ask.kind === 'choice') {
        const quoted = (ask.options || []).map((o) => '"' + o + '"').join(', ')
        text =
          'Not done yet: Simone asks the user a question on the screen and waits for their answer' +
          (ask.prompt ? ': "' + ask.prompt + '" ' : '. ') +
          (quoted ? 'The options: ' + quoted + '. ' : '') +
          'Tell the user the question: they choose their answer on the screen. ' +
          'You cannot choose it for them, and asking the workspace again does not answer it. ' +
          'Never say it is done.'
      } else {
        const label = typeof ask.label === 'string' ? ask.label.trim() : ''
        const what =
          label ||
          String(ask.tool || 'a step')
            .replace(/^module_fn_/, '')
            .replace(/_/g, ' ')
        // The buttons by the words the screen shows, in the page language.
        const word = (key) => {
          let raw = ''
          try {
            raw = overlay.call('polyglot', key)
          } catch (_) {
            raw = ''
          }
          return typeof raw === 'string' && raw && raw !== key ? raw : ''
        }
        const run = word('ai.tool.run')
        const skip = word('ai.tool.skip')
        const press =
          run && skip
            ? 'they press "' + run + '" or "' + skip + '" on the screen. '
            : 'they answer it on the screen. '
        text =
          'Not done yet: "' +
          what +
          '" waits for the user’s approval on the screen and has not run. ' +
          'Tell the user it waits for their OK: ' +
          press +
          'You cannot approve it, and asking the workspace again does not approve it. ' +
          'Never say it is done.'
      }
      if (!tool.answered) {
        clearTimeout(tool.timer)
        tool.answered = true
        tool.resolve(text)
      } else if (v.agent && typeof v.agent.sendContextualUpdate === 'function') {
        try {
          v.agent.sendContextualUpdate(text)
        } catch (_) {}
      }
      // The tool is answered: the agent talks on while the ask waits.
      const root = this.rootOf(overlay)
      if (root && root.voiceStatus === 'thinking' && !(v.agentShows > 0)) {
        this.up(overlay, { voiceStatus: 'listening' })
      }
    },

    // show_page — Fly makes the page the agent was asked for on the
    // workspace canvas, as a spoken Fly ask does in the loop; the call steps
    // down to the island so the page is in view. Resolves within 25 s:
    // `ready` (the page's title and opening), `building` (it keeps building;
    // when it lands the agent hears it as context), `unavailable` (no Fly
    // here — the agent asks Simone instead) or `failed`.
    agentShow(overlay, params) {
      const v = this.voiceOf(overlay)
      const root = this.rootOf(overlay)
      const fns = (overlay.context && overlay.context.functions) || {}
      const request = String((params && params.request) || '')
        .replace(/\s+/g, ' ')
        .trim()
      if (!request) return Promise.resolve({ status: 'failed', reason: 'empty request' })
      if (
        !root ||
        root._aiBoardsAvailable !== true ||
        typeof fns.assistantFlyGenerate !== 'function'
      ) {
        return Promise.resolve({ status: 'unavailable' })
      }
      const token = v.agentToken
      v.agentShows = (v.agentShows || 0) + 1
      this.up(overlay, { voiceStatus: 'thinking', voiceMinimized: true, voiceHost: null })
      const resultOf = (page) => {
        if (page && page.status === 'ready') {
          return {
            status: 'ready',
            title: String(page.title || '').trim(),
            opening: String(page.opening || '').trim()
          }
        }
        const why = (page && page.error) || 'failed'
        return why === 'unavailable' ? { status: 'unavailable' } : { status: 'failed', reason: why }
      }
      return new Promise((resolve) => {
        let answered = false
        const timer = setTimeout(() => {
          answered = true
          resolve({ status: 'building' })
        }, 25000)
        Promise.resolve()
          .then(() => overlay.call('assistantFlyGenerate', request))
          .catch(() => null)
          .then((page) => {
            clearTimeout(timer)
            v.agentShows = Math.max(0, (v.agentShows || 1) - 1)
            const live = v.agentToken === token && !!v.active
            const out = resultOf(page)
            if (live && out.status === 'ready' && out.title) this.actionTurn(overlay, out.title)
            if (!answered) {
              answered = true
              resolve(out)
              return
            }
            if (!live || !v.agent || typeof v.agent.sendContextualUpdate !== 'function') return
            try {
              v.agent.sendContextualUpdate(
                out.status === 'ready'
                  ? 'The page "' + out.title + '" is on the screen now.'
                  : 'The page could not be built (' + (out.reason || out.status) + ').'
              )
            } catch (_) {}
          })
      })
    },

    // Stop speaking on the live call. WebRTC has no client-side cut (the
    // client's own interrupt is a no-op there; the agent stops when the
    // PERSON speaks): the reply's sound goes to zero and the agent hears the
    // person is active (it holds its next turn a moment). The sound comes
    // back on the agent's next listening edge or the person's next words —
    // after 15 s at the latest, so a missing edge never leaves the call mute.
    agentStopSpeaking(overlay) {
      const v = this.voiceOf(overlay)
      const conv = v.agent
      if (!conv) return false
      try {
        conv.setVolume({ volume: 0 })
        v.agentMuted = true
      } catch (_) {}
      try {
        conv.sendUserActivity()
      } catch (_) {}
      clearTimeout(v.agentUnmute)
      v.agentUnmute = setTimeout(() => this.agentRestoreVolume(v), 15000)
      this.up(overlay, { voiceStatus: 'listening' })
      return true
    },

    agentRestoreVolume(v) {
      clearTimeout(v.agentUnmute)
      v.agentUnmute = null
      if (!v.agentMuted) return
      v.agentMuted = false
      try {
        if (v.agent) v.agent.setVolume({ volume: 1 })
      } catch (_) {}
    },

    // The input axis on the live call (voiceMicToggle): the agent stops
    // hearing; the call goes on.
    agentMicMuted(overlay, muted) {
      const v = this.voiceOf(overlay)
      if (!v.agent || typeof v.agent.setMicMuted !== 'function') return false
      try {
        v.agent.setMicMuted(!!muted)
      } catch (_) {
        return false
      }
      return true
    },

    // End the live call and release what it holds. Clearing the token first
    // makes every callback of this attempt — and an attempt still connecting
    // — abandon itself. A tool still out is answered, so the agent never
    // waits on a call that is gone.
    stopAgent(overlay) {
      const v = this.voiceOf(overlay)
      const conv = v.agent
      v.agent = null
      v.agentToken = null
      clearTimeout(v.agentWatch)
      v.agentWatch = null
      clearTimeout(v.agentPaid)
      v.agentPaid = null
      clearTimeout(v.agentUnmute)
      v.agentUnmute = null
      v.agentMuted = false
      v.agentTurnId = null
      v.agentShows = 0
      const turn = v.awaiting
      if (turn && turn.tool) {
        v.awaiting = null
        clearInterval(v.turnPoll)
        v.turnPoll = null
        clearTimeout(turn.tool.timer)
        if (!turn.tool.answered) {
          turn.tool.answered = true
          try {
            turn.tool.resolve('Error: the call ended.')
          } catch (_) {}
        }
      }
      if (v.analyser && v.analyser.__agent) {
        v.analyser = null
        v.bins = null
      }
      if (conv) {
        try {
          const r = conv.endSession()
          if (r && typeof r.catch === 'function') r.catch(() => {})
        } catch (_) {}
      }
    },

    // Engine switch:
    //   COMMAND — the one-shot server pipeline (recorder → Whisper → host
    //             grammar / Simone → 11labs → close).
    //   TALK    — the live voice agent (`startAgentEngine`, Nika 2026-10-26:
    //             "11labs talks, Simone acts"). Where the agent is not
    //             available, the SIMONE loop on the SAME server pipeline as
    //             command (VOICE-TALK-ONLY-SIMONE-1 part 3, route A): recorder
    //             → Whisper → Simone (sendPrompt → sdk.ai transport) → 11labs
    //             speaks the FINAL turn text → recorder … until ✕/Esc.
    // Any (re)start — Try again, a mode switch — ends a live agent call first.
    startEngine(overlay) {
      const root = this.rootOf(overlay)
      this.stopAgent(overlay)
      if (!root || root.voiceMode !== 'talk') {
        this.up(overlay, { voiceEngineReason: 'command-mode' })
        this.startRecorderEngine(overlay, 'recorder')
        return
      }
      this.up(overlay, { voiceEngineReason: null })
      const sdk = this.sdkOf(overlay)
      if (sdk && typeof sdk.voiceAgentSession === 'function') {
        this.startAgentEngine(overlay)
        return
      }
      this.startRecorderEngine(overlay, 'simone')
    },

    // WAI-ARIA dialog-close focus restore — the other half of `syncFocus`
    // above (which moves focus IN when the full surface opens). Give back
    // whatever had focus when the session STARTED — `voiceStart` (in
    // voiceSession.js) captures it before the overlay/strip/island ever
    // takes over. Idempotent (clears `focusOrigin` first) and defensive: a
    // node that unmounted mid-session (route change, size toggle) is
    // silently skipped rather than thrown on.
    restoreFocus(v) {
      const node = v.focusOrigin
      v.focusOrigin = null
      if (node && node.isConnected && typeof node.focus === 'function') {
        try {
          node.focus()
        } catch (_) {}
      }
    },

    teardown(overlay) {
      const v = this.voiceOf(overlay)
      v.active = false
      this.stopAgent(overlay)
      v.awaiting = null
      v.closeAfterCommit = false
      clearTimeout(v.doneTimer)
      clearTimeout(v.stopTimer)
      clearInterval(v.turnPoll)
      v.turnPoll = null
      this.stopBargeWatch(v)
      this.stopRecorder(v)
      this.stopSpeaking(overlay)
      this.stopTracks(v)
      v.engine = null
      this.up(overlay, {
        voiceOpen: false,
        voiceMinimized: false,
        // Session end releases the composer — every voiceHost reader ANDs
        // with voiceOpen so this is pure hygiene, but it closes the
        // stale-host class: the next open starts from null unless ITS
        // gesture sets one.
        voiceHost: null,
        voiceStatus: 'idle',
        voiceInterim: '',
        voiceResult: '',
        voiceMicMuted: false,
        voiceEngine: null,
        voiceEngineReason: null,
        voiceStartedAt: 0
      })
      this.restoreFocus(v)
    }
  },

  position: 'fixed',
  inset: '0',
  zIndex: '10600',
  flow: 'y',
  align: 'center center',
  // The header floats over the top edge (CallHeader, absolute); the column
  // keeps clear of it on a short screen.
  padding: 'D B C',
  boxSizing: 'border-box',
  background: 'black.6',
  // style-object form required for backdrop-filter (css-in-props leak bug).
  style: {
    backdropFilter: 'blur(28px) saturate(1.2)',
    WebkitBackdropFilter: 'blur(28px) saturate(1.2)'
  },
  // The Siri surface is dark in both schemes.
  themeModifier: 'dark',

  opacity: '0',
  visibility: 'hidden',
  pointerEvents: 'none',
  transition: 'C opacity defaultBezier, C visibility defaultBezier',
  // Visible only as the FULL surface: `voiceMinimized` hands rendering to the
  // island (SimoneReasoningPill) and `voiceHost` (composer strip, T18) hands it
  // to the composer — in both cases the session keeps running here (this
  // element stays the engine host: scope handles, onStateUpdate watcher, Esc
  // binding all live on). Guarded reads: either key may not exist on every
  // root; absence means the classic full-screen behavior.
  isActive: (el, s) =>
    !!(s.root && s.root.voiceOpen && !s.root.voiceMinimized && !s.root.voiceHost),
  '.isActive': { opacity: '1', visibility: 'visible', pointerEvents: 'auto' },

  role: 'dialog',
  ariaModal: 'true',
  aria: { label: (el) => el.call('polyglot', 'shell.voice.call.title') },

  onInit: (el, s) => {
    const root = (s && s.root) || s
    // Announce the host so mic buttons render (Chat surfaces without this
    // overlay — chrome-connect, ext-ui — never show a mic).
    if (root && root.update) root.update({ voiceHostReady: true }, { preventFetch: true })
  },
  // Escape takes away the TOP voice layer, and never ends a call by itself
  // (2026-10-22, the call redesign): the call screen steps down to the island
  // (voiceMinimize — the call goes on); the composer strip, which replaced the
  // text input, gives the input back by ending the voice input it is; the
  // island is ambient, so there Escape belongs to whatever the page has open.
  // A genuine window-level browser event, so it is DECLARED (FA503):
  // registered once per instance, removed in dispose().
  onWindowKeydown: (e, el) => {
    if (e.key !== 'Escape') return
    const st = el.state && (el.state.root || el.state)
    if (!st || !st.voiceOpen) return
    if (st.voiceHost) el.call('voiceStop')
    else if (!st.voiceMinimized) el.call('voiceMinimize')
  },
  onBeforeRemove: (el) => el.call('voiceTeardown'),
  // ——— the state watcher (VOICE-OVERLAY-STATE-HOOK-DEAD-1) ———
  //
  // `onStateUpdate` in DOMQL v3.14 is a DECLARATIVE effect, not an event:
  // `smbls/packages/element/src/create.js` registers it ONLY when a sibling
  // `stateDeps` ARRAY is present (`typeof element.onStateUpdate === 'function'
  // && Array.isArray(element.stateDeps)`), and no legacy `stateUpdate` event
  // is emitted anywhere in the framework. This block shipped for months with
  // NO `stateDeps` and the legacy `(changes, el)` signature, so it never ran
  // once: focus never entered the dialog, the voice⟷chat exclusivity never
  // asserted, and a Talk turn resolved on the 500 ms `turnPoll` backstop
  // ALONE (an up-to-500 ms lag before Simone's answer is spoken).
  //
  // Every selector below is read UP FRONT inside the framework's own effect,
  // so the subscription never depends on the handler's control flow (the
  // conditional-read tracking gap `stateDeps` exists to close). They are also
  // each self-contained — a `stateDeps` entry is serialized on its own by
  // frank.toJSON, exactly like the props around it, so a shared module-scope
  // root-resolver would read as `undefined` on the platform payload (FA204,
  // project_frank_drops_module_local_helpers — the same trap that moved
  // `syncChatSuppression` onto `scope`).
  //
  // What each dep is for:
  //   • voiceOpen / voiceMinimized / voiceHost — the posture axes both
  //     `syncFocus` (full-surface rising edge) and `syncChatSuppression`
  //     (foreground rising edge, session-end falling edge) are edge-triggered on.
  //   • `<prefix>chatTyping` / `<prefix>chatMessages` for all three thread
  //     prefixes `chatPrefix()` can produce ('' general | '_ax_' add-app |
  //     '_db_' dashboard) — the awaited prefix is only known at runtime
  //     (`v.awaiting.k`), and a dep read must not be conditional, so all
  //     three are subscribed. Reading a key a given root never declares is
  //     safe and mints a lazy signal (signal/store.js), so canvas/preview
  //     roots take no write and still re-fire if the key ever lands.
  //   • freestyleBusy / freestyleError — the other awaited turn type.
  // Deliberately NOT deps: `assistantOpen`/`assistantDock`. The handler WRITES
  // `assistantDock`; watching it would re-enter the effect on its own write.
  stateDeps: [
    (el, s) => ((s && (s.root || s)) || {}).voiceOpen,
    (el, s) => ((s && (s.root || s)) || {}).voiceMinimized,
    (el, s) => ((s && (s.root || s)) || {}).voiceHost,
    (el, s) => ((s && (s.root || s)) || {}).chatTyping,
    (el, s) => ((s && (s.root || s)) || {})._ax_chatTyping,
    (el, s) => ((s && (s.root || s)) || {})._db_chatTyping,
    (el, s) => ((s && (s.root || s)) || {}).chatMessages,
    (el, s) => ((s && (s.root || s)) || {})._ax_chatMessages,
    (el, s) => ((s && (s.root || s)) || {})._db_chatMessages,
    (el, s) => ((s && (s.root || s)) || {}).freestyleBusy,
    (el, s) => ((s && (s.root || s)) || {}).freestyleError
  ],
  // Watches async turns the session started (assistant reply, freestyle build),
  // then runs the voice⟷chat exclusivity choreography (see _syncChatSuppression
  // above) — engine watcher first, so the turn resolution a dock write triggers
  // re-entry for has already been processed. v3.14 signature:
  // `(el, state, context, { prev, next })`.
  onStateUpdate: (el) => {
    el.call('voiceOnStateChange')
    el.scope.syncChatSuppression(el)
    el.scope.syncFocus(el)
  },
  // A click on the scrim steps down to the island, like Escape — a stray
  // click beside the controls must never hang up. (While minimized the root is
  // invisible with pointerEvents none; the guard covers a synthetic click
  // during the posture swap.)
  onClick: (e, el, s) => {
    if (e.target !== el.node) return
    if (s.root && s.root.voiceMinimized) return
    el.call('voiceMinimize')
  },

  // The edge glow is cached once — the frame loop below runs at 60 fps and
  // must not walk the tree (the orb ring and the clock are voiceCallFrame's,
  // which caches its own).
  onRender: (el) => {
    if (!el.node) return
    el.scope.voiceGlowEl = (el.lookdown && el.lookdown('Glow')) || null
  },

  onFrame: (el, s) => {
    const root = (s && s.root) || s
    if (!root || !root.voiceOpen) return
    // Strip-hosted (T18) or minimized (the island runs the call chrome off
    // its own frame loop): the screen is invisible — skip the 60fps work.
    if (root.voiceHost || root.voiceMinimized) return
    // The orb's ring and the clock; answers the live mic level.
    const level = el.call('voiceCallFrame') || 0
    const sc = el.scope
    sc.voiceT = (sc.voiceT || 0) + 1 / 60
    const t = sc.voiceT
    const glow = sc.voiceGlowEl
    if (glow && glow.setNodeStyles) {
      const status = root.voiceStatus
      const speakPulse = status === 'speaking' ? 0.12 * Math.abs(Math.sin(t * 4.4)) : 0
      glow.setNodeStyles({
        opacity: Math.min(1, 0.5 + level * 1.5 + speakPulse).toFixed(3),
        filter: 'blur(52px) saturate(1.35) hue-rotate(' + Math.round((t * 16) % 360) + 'deg)'
      })
    }
  },

  // ——— visuals ———

  // Edge glow — brand conic gradient masked away from the center so it hugs
  // the viewport rim, exactly the Apple-Intelligence treatment.
  Glow: {
    position: 'absolute',
    inset: '0',
    pointerEvents: 'none',
    backgroundImage: 'themeDark',
    opacity: '.5',
    // flat, so the atomic engine adds the -webkit- twin (an inline style block never gets one)
    maskImage: 'radial-gradient(ellipse at center, transparent 46%, black 86%)',
    style: {
      filter: 'blur(52px) saturate(1.35)',
      willChange: 'filter, opacity'
    }
  },

  // ——— the call screen (2026-10-22): who you are talking to and the call's
  // clock across the top, the orb in the middle, the conversation as
  // captions under it, and the three call controls at the bottom — the
  // anatomy of the Fly call Nika approved for shaker, on this surface's own
  // dark scrim and edge glow. Every piece is the SHARED call chrome
  // (components/VoiceControls.js) the two bars carry too.

  // The top edge: the identity on the left; the clock and Minimize on the
  // right. Minimize sits where a dismiss is looked for, and it is the one
  // dismiss that cannot hang up.
  CallHeader: {
    position: 'absolute',
    top: 'B',
    left: 'B',
    right: 'B',
    flow: 'x',
    align: 'center space-between',
    gap: 'A',

    // Which engine runs is said here, honestly (the engine tag's job,
    // 2026-08-02 per Nika "not sure if elevenlabs agent is speaking"): talk
    // is Simone, the one-shot hold is a voice command. The tooltip carries the
    // full sentence.
    CallIdentity: {
      flow: 'y',
      gap: 'W',
      minWidth: '0',
      titleDeps: ['voiceMode'],
      title: (el, s) =>
        el.call(
          'polyglot',
          (s.root && s.root.voiceMode) === 'talk'
            ? 'shell.voice.engine.whyTalk'
            : 'shell.voice.engine.whyCommand'
        ),
      CallName: {
        tag: 'span',
        fontSize: 'A',
        fontWeight: '500',
        color: 'title',
        whiteSpace: 'nowrap',
        textDeps: ['voiceMode'],
        // `Simone` is the agent's own NAME and stays Latin (glossary rule 4).
        text: (el, s) =>
          (s.root && s.root.voiceMode) === 'talk'
            ? 'Simone'
            : el.call('polyglot', 'shell.voice.engine.voice')
      },
      CallKind: {
        tag: 'span',
        fontSize: 'Z',
        color: 'caption',
        whiteSpace: 'nowrap',
        textDeps: ['voiceMode'],
        text: (el, s) =>
          el.call(
            'polyglot',
            (s.root && s.root.voiceMode) === 'talk'
              ? 'shell.voice.call.title'
              : 'shell.voice.call.commandTitle'
          )
      }
    },

    CallTrail: {
      flow: 'x',
      align: 'center flex-end',
      gap: 'Z',
      flexShrink: '0',
      VoiceCallClock: { fontSize: 'Z1' },
      // The dialog's focus move-in (scope syncFocus) lands here — the safe
      // control, never the hang-up.
      CallMinimize: {
        extends: 'NavbarButton',
        icon: 'minimize',
        title: (el) => el.call('polyglot', 'shell.voice.call.minimize'),
        onClick: (e, el) => {
          e.stopPropagation()
          el.call('voiceMinimize')
        }
      }
    }
  },

  Center: {
    flow: 'y',
    align: 'center center',
    gap: 'A',
    // Column gauge — the SAME `H` token as the bottom chat prompt
    // (CanvasPromptTextarea's base width), so every posture of the call
    // shares the composer stack's footprint (2026-08-06 per Nika "give voice
    // ai the same maxwidth as chatprompt").
    maxWidth: 'H',
    maxHeight: '100%',
    minHeight: '0',
    padding: 'B',
    boxSizing: 'border-box',
    position: 'relative',
    width: '100%',
    transform: 'translateY(var(--spacing-A2))',
    transition: 'C transform defaultBezier',
    isActive: (el, s) => !!(s.root && s.root.voiceOpen),
    '.isActive': { transform: 'translateY(0)' },

    // The orb at call size; its ring breathes with the mic and the reply.
    VoiceOrb: {
      boxSize: 'D1',
      marginBottom: 'Z',
      VoiceOrbCore: { Icon: { boxSize: '38%' } }
    },

    // The lifecycle word, large — the clock is in the header. Here it wraps:
    // an error is read in full on the call screen (a bar cuts it to a line).
    VoiceCallStatus: {
      align: 'center center',
      maxWidth: '100%',
      VoiceCallWord: { fontSize: 'A2', textAlign: 'center', whiteSpace: 'normal' },
      // One clock per screen — it is in the header.
      VoiceCallClock: null
    },

    // THE CAPTIONS — the shared transcript (components/VoiceLog.js), at the
    // screen's reading size; the newest line reads first. The only place the
    // answers appear (2026-08-06 per Nika "do not mix answering with two
    // sections").
    VoiceLog: {
      fontSize: 'A1',
      maxHeight: 'F',
      flexShrink: '1',
      minHeight: '0',
      aria: { label: (el) => el.call('polyglot', 'shell.voice.call.transcript') }
    },

    // Live partial speech — reserved for engines that emit interims (the
    // server pipeline commits whole utterances, so this is usually empty).
    Interim: {
      tag: 'p',
      fontSize: 'B1',
      color: 'caption',
      lineHeight: '1.35',
      textAlign: 'center',
      aria: { live: 'polite' },
      textDeps: ['voiceInterim'],
      text: (el, s) => (s.root && s.root.voiceInterim) || ''
    },

    // THE ASK — what waits on the person, as the island shows it (the shared
    // AiAsk: a step with what it will write and Run · Skip · Run all on the
    // row's own settle, or a question with one button per option on the
    // card's own writer, and a live region that says "Needs your OK" or
    // "Waiting for your choice"). The island and the strip are hidden while
    // this screen is up, and the live agent keeps it up for its whole ask, so
    // the call shows the ask itself. In the column's flow, under the words:
    // it comes once per ask, and the column re-centres as the captions grow
    // anyway — a box kept for it would sit empty on every other turn.
    AiAsk: {},

    // The turn's one contextual action — Stop speaking while the reply plays,
    // Try again after an error. The slot keeps its box when empty (Rule 66):
    // the controls under it never jump as the assistant starts and stops
    // talking. The hidden button is out of the tab order and the tree.
    CallTurnSlot: {
      flow: 'x',
      align: 'center center',
      // Exactly the pill's height, so showing it moves nothing (measured: a
      // min-height slot grew 3px under the pill and nudged the whole column).
      height: 'B2',
      childExtends: 'Button',
      childProps: {
        flow: 'x',
        gap: 'Y2',
        align: 'center center',
        height: 'B2',
        boxSizing: 'border-box',
        padding: '0 A1',
        round: 'radiusPill',
        theme: 'transparent',
        background: 'white.12',
        color: 'title',
        fontSize: 'Z1',
        fontWeight: '500',
        flexShrink: '0',
        ':hover': { background: 'white.2' }
      },

      StopSpeaking: {
        display: null,
        showDeps: ['voiceStatus'],
        show: (el, s) => !!(s.root && s.root.voiceStatus === 'speaking'),
        aria: { label: (el) => el.call('polyglot', 'shell.voice.stopSpeaking') },
        Icon: { name: 'stop' },
        Label: { tag: 'span', text: '{{ shell.voice.stopSpeaking | polyglot }}' },
        onClick: (e, el) => {
          e.stopPropagation()
          el.call('voiceStopSpeaking')
        }
      },

      TryAgain: {
        display: null,
        showDeps: ['voiceStatus'],
        show: (el, s) => !!(s.root && s.root.voiceStatus === 'error'),
        aria: { label: (el) => el.call('polyglot', 'shell.voice.retry') },
        Icon: { name: 'refresh' },
        Label: { tag: 'span', text: '{{ shell.voice.retry | polyglot }}' },
        onClick: (e, el) => {
          e.stopPropagation()
          el.call('voiceRetry')
        }
      }
    },

    // Type · End · Mute.
    VoiceCallActions: {},

    // The hint sits on the dark scrim, not on a scheme surface: `themeModifier:
    // 'dark'` forces only this surface's own `theme`, so an adaptive `caption`
    // here resolved the LIGHT scheme's near-black ink on the dark scrim
    // (1.52:1 measured live over the sky, served 12a4f7826), and the old extra
    // .7 opacity sank it further. The ink is the dark surface's caption
    // (gray13); in light the `black.6` scrim over a white card leaves a
    // mid-grey ground (rgb 102), so the light scheme takes the next step up
    // (gray15) - both clear 4.5:1 at the worst ground
    // (__tests__/voiceHintInk.test.js). No opacity on text.
    Hint: {
      tag: 'span',
      fontSize: 'Z',
      color: 'gray13',
      '@light': { color: 'gray15' },
      textAlign: 'center',
      text: (el, s) => {
        const r = s.root || s
        return el.call(
          'polyglot',
          r.voiceMode === 'talk' ? 'shell.voice.hint.talk' : 'shell.voice.hint.command'
        )
      }
    }
  }
}
